/**
 * Render a policy time window in the viewer's own clock.
 *
 * THE MISMATCH THIS EXISTS TO CLOSE
 *
 * Layer 3 policy rules evaluate in UTC, deliberately: one window for the whole
 * fleet, not one per user. Everything this app displays, however, goes through
 * `datetime.ts` and is rendered in the viewer's IANA zone.
 *
 * So a user in Asia/Kolkata whose own clock reads 11:00 is told their deletion
 * was "blocked outside business hours" — and there is nothing on screen they
 * can reconcile that against, because 11:00 plainly is business hours to them.
 * The policy is not wrong; the message is unanswerable.
 *
 * The server sends the window and names its zone. The conversion happens here,
 * on the client, for the same reason the evaluation does not: a window that
 * means something different per user would be a different policy. Only the
 * explanation is localised.
 */

import { userTimeZone } from '@/lib/datetime';

export interface PolicyWindow {
  rule?: string | null;
  /** Always 'UTC' today. Named rather than assumed — that assumption was the bug. */
  timezone: string;
  /** Hours during which the action IS permitted, in `timezone`. */
  allowed_hours?: number[];
  /** ISO 8601 weekdays during which it IS permitted. Monday = 1. */
  allowed_weekdays?: number[];
}

export interface PolicyDenial {
  code?: string;
  message?: string;
  policy_rules?: string[];
  policy_window?: PolicyWindow;
}

const DAY = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** True when an API error body is a Layer 3 policy denial rather than a plain 403. */
export function isPolicyDenial(detail: unknown): detail is PolicyDenial {
  return !!detail && typeof detail === 'object'
    && (detail as PolicyDenial).code === 'policy_rule_denied';
}

/**
 * Shift a set of whole hours from one zone into the viewer's.
 *
 * Uses a real date rather than arithmetic on the offset: half-hour zones
 * (Asia/Kolkata is +05:30) and DST both make "add N hours" wrong. The shift is
 * measured by formatting a known instant in both zones and taking the
 * difference, so whatever Intl says is what we use.
 */
function shiftHours(hours: number[], fromZone: string, toZone: string): { hours: number[]; dayShift: number; minuteOffset: number } {
  const probe = new Date();
  const readHour = (tz: string) =>
    Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hour12: false, timeZone: tz }).format(probe));
  const readMinute = (tz: string) =>
    Number(new Intl.DateTimeFormat('en-GB', { minute: '2-digit', timeZone: tz }).format(probe));

  let deltaH = 0;
  let deltaM = 0;
  try {
    deltaH = readHour(toZone) - readHour(fromZone);
    deltaM = readMinute(toZone) - readMinute(fromZone);
  } catch {
    return { hours, dayShift: 0, minuteOffset: 0 };
  }
  // Normalise a wrap across midnight (e.g. 01 - 22 = -21, which is really +3).
  if (deltaH > 12) deltaH -= 24;
  if (deltaH < -12) deltaH += 24;
  if (deltaM < 0) { deltaM += 60; deltaH -= 1; }

  let dayShift = 0;
  const shifted = hours.map((h) => {
    const v = h + deltaH;
    if (v < 0) dayShift = -1;
    if (v > 23) dayShift = 1;
    return ((v % 24) + 24) % 24;
  });
  return { hours: shifted, dayShift, minuteOffset: deltaM };
}

function hhmm(h: number, m = 0): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Contiguous runs, so [8,9,10,16,17] reads "08:00-11:00 and 16:00-18:00". */
function ranges(hours: number[]): Array<[number, number]> {
  const sorted = [...new Set(hours)].sort((a, b) => a - b);
  const out: Array<[number, number]> = [];
  for (const h of sorted) {
    const last = out[out.length - 1];
    if (last && h === last[1] + 1) last[1] = h;
    else out.push([h, h]);
  }
  return out;
}

/**
 * A sentence the user can act on.
 *
 *   "Deleting is allowed 13:30-22:30 (your time), Monday to Friday.
 *    The policy window is 08:00-17:00 UTC."
 *
 * Returns null when there is no window to describe, so callers fall back to the
 * server's own message rather than printing an empty explanation.
 */
export function describePolicyWindow(w: PolicyWindow | undefined | null): string | null {
  if (!w) return null;
  const hours = w.allowed_hours ?? [];
  const days = w.allowed_weekdays ?? [];
  if (!hours.length && !days.length) return null;

  const tz = userTimeZone();
  const parts: string[] = [];

  if (hours.length) {
    const { hours: local, dayShift, minuteOffset } = shiftHours(hours, w.timezone, tz);
    const sameZone = tz === w.timezone;
    const localText = ranges(local)
      .map(([a, b]) => `${hhmm(a, minuteOffset)}–${hhmm((b + 1) % 24, minuteOffset)}`)
      .join(' and ');
    const srcText = ranges(hours).map(([a, b]) => `${hhmm(a)}–${hhmm((b + 1) % 24)}`).join(' and ');

    if (sameZone) {
      parts.push(`Allowed ${srcText} ${w.timezone}.`);
    } else {
      const spill = dayShift === 1 ? ' the next day' : dayShift === -1 ? ' the previous day' : '';
      parts.push(`Allowed ${localText} your time${spill} — the policy window is ${srcText} ${w.timezone}.`);
    }
  }

  if (days.length) {
    const named = [...new Set(days)].sort((a, b) => a - b).map((d) => DAY[d]).filter(Boolean);
    if (named.length) {
      const contiguous = named.length > 2 && days.length === (Math.max(...days) - Math.min(...days) + 1);
      parts.push(contiguous
        ? `${named[0]} to ${named[named.length - 1]} only.`
        : `${named.join(', ')} only.`);
    }
  }

  return parts.join(' ');
}

/** The full message for a denial: what was blocked, by which rule, and when it is allowed. */
export function policyDenialMessage(detail: PolicyDenial): string {
  const base = detail.message || 'Blocked by policy.';
  const when = describePolicyWindow(detail.policy_window);
  return when ? `${base} ${when}` : base;
}
